'use client';

import styles from './tool-filter-select.module.scss';

export type ToolRawOption = Record<string, unknown> | null | undefined;

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const normalizeToolOption = (
  option: ToolRawOption,
  fallbackValue?: string | number | boolean
) => {
  const record = option && typeof option === 'object' ? option : {};
  const rawValue =
    (record as Record<string, unknown>).value ??
    (record as Record<string, unknown>).id ??
    (record as Record<string, unknown>).legacyId ??
    (record as Record<string, unknown>).key ??
    (record as Record<string, unknown>).name ??
    fallbackValue;

  if (rawValue === undefined || rawValue === null) return null;
  const value = String(rawValue);

  const rawLabel =
    (record as Record<string, unknown>).label ??
    (record as Record<string, unknown>).name ??
    (record as Record<string, unknown>).title ??
    (record as Record<string, unknown>).text;
  const label = isNonEmptyString(rawLabel) ? rawLabel : value;

  return { value, label };
};

export type ToolFilterSelectProps = {
  label: string;
  options?: ToolRawOption[];
  value: string | number | boolean | null | undefined;
  onChange: (next: string) => void;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
};

export function ToolFilterSelect({
  label,
  options,
  value,
  onChange,
  className,
  labelClassName,
  selectClassName
}: ToolFilterSelectProps) {
  const fieldClassName = [styles.filterField, className]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={fieldClassName}>
      <span
        className={[styles.filterLabel, labelClassName]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </span>
      <select
        className={[styles.filterSelect, selectClassName]
          .filter(Boolean)
          .join(' ')}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {(options ?? []).map((option, idx) => {
          const normalized = normalizeToolOption(option, `option-${idx}`);
          if (!normalized) return null;
          return (
            <option key={`${idx}-${normalized.value}`} value={normalized.value}>
              {normalized.label}
            </option>
          );
        })}
      </select>
    </label>
  );
}
